/**
 * @module storeTransaction
 * @description Atomic Transaction Utility for Svelte Stores
 *
 * Groups multiple store mutations and performs automatic rollback
 * on error, ensuring state consistency.
 *
 * @example
 * // Simple usage with withTransaction
 * await withTransaction(
 *   [selectedProject, selectedDoc, docs],
 *   async () => {
 *     selectedProject.set(project);
 *     selectedDoc.set(null);
 *     await loadDocs(project.id);
 *   }
 * );
 *
 * @example
 * // Manual usage with createTransaction
 * const tx = createTransaction([store1, store2]);
 * try {
 *   store1.set(newValue1);
 *   store2.set(newValue2);
 *   tx.commit();
 * } catch (error) {
 *   tx.rollback();
 *   throw error;
 * }
 */

import { get, type Writable } from "svelte/store";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Transaction object for managing atomic store operations
 */
export interface Transaction {
  /** Commits the transaction (changes are preserved) */
  commit(): void;
  /** Rolls back the transaction and restores initial state */
  rollback(): void;
  /** Checks if the transaction is still active */
  isActive(): boolean;
  /** Gets the snapshot of a specific store */
  getSnapshot<T>(store: Writable<T>): T | undefined;
}

/**
 * Options for withTransaction
 */
export interface TransactionOptions<T = unknown> {
  /** Callback called on rollback */
  onRollback?: (error: Error) => void;
  /** Callback called on successful commit */
  onCommit?: (result: T) => void;
}

/**
 * Savepoint data structure
 */
interface Savepoint<T> {
  name: string;
  value: T;
  timestamp: number;
}

/**
 * Public savepoint info (without value)
 */
export interface SavepointInfo {
  name: string;
  timestamp: number;
}

/**
 * Store with savepoint capabilities
 */
export interface SavepointStore<T> extends Writable<T> {
  /** Creates a savepoint with the specified name */
  savepoint(name: string): void;
  /** Rolls back to the specified savepoint */
  rollbackTo(name: string): boolean;
  /** Removes a savepoint without rollback */
  releaseSavepoint(name: string): void;
  /** Lists all active savepoints */
  getSavepoints(): SavepointInfo[];
  /** Removes all savepoints */
  clearSavepoints(): void;
}

/**
 * Generic actions object type
 */
type ActionsObject = Record<string, unknown>;

/**
 * Store mapping for transactional actions
 */
type StoreMapping = Record<string, Writable<unknown>[]>;

// ============================================================================
// TRANSACTION FUNCTIONS
// ============================================================================

/**
 * Creates a transaction for a set of stores
 * Captures the current state of each store to enable rollback
 *
 * @param stores - Stores to include in the transaction
 * @returns Transaction object with commit and rollback methods
 */
export function createTransaction(stores: Writable<unknown>[]): Transaction {
  // Capture initial state of each store
  const snapshots = new Map<Writable<unknown>, unknown>();

  for (const store of stores) {
    snapshots.set(store, get(store));
  }

  let committed = false;
  let rolledBack = false;

  return {
    commit(): void {
      if (rolledBack) {
        throw new Error("Cannot commit a rolled back transaction");
      }
      committed = true;
      snapshots.clear();
    },

    rollback(): void {
      if (committed) {
        throw new Error("Cannot rollback a committed transaction");
      }

      // Restore initial state of each store
      for (const [store, snapshot] of snapshots) {
        store.set(snapshot);
      }

      rolledBack = true;
      snapshots.clear();
    },

    isActive(): boolean {
      return !committed && !rolledBack;
    },

    getSnapshot<T>(store: Writable<T>): T | undefined {
      return snapshots.get(store as Writable<unknown>) as T | undefined;
    },
  };
}

/**
 * Executes a function within an atomic transaction
 * Automatic rollback on error
 *
 * @param stores - Involved stores
 * @param fn - Function to execute (can be async)
 * @param options - Transaction options
 * @returns Function result
 * @throws Rethrows the error after rollback
 */
export async function withTransaction<T>(
  stores: Writable<unknown>[],
  fn: (tx: Transaction) => T | Promise<T>,
  options: TransactionOptions<T> = {},
): Promise<T> {
  const { onRollback, onCommit } = options;
  const tx = createTransaction(stores);

  try {
    const result = await fn(tx);
    tx.commit();

    if (onCommit) {
      onCommit(result);
    }

    return result;
  } catch (error) {
    tx.rollback();

    if (onRollback) {
      onRollback(error as Error);
    }

    throw error;
  }
}

/**
 * Creates a transactional wrapper for an actions object
 * Allows defining which stores are involved for each action
 *
 * @param actions - Object containing actions
 * @param storeMapping - Mapping action -> involved stores
 * @returns Actions wrapped with transactions
 *
 * @example
 * const transactionalActions = createTransactionalActions(
 *   appActions,
 *   {
 *     selectProject: [selectedProject, selectedDoc, docs],
 *     updateDoc: [docs, selectedDoc],
 *   }
 * );
 */
export function createTransactionalActions<T extends ActionsObject>(
  actions: T,
  storeMapping: StoreMapping,
): T {
  const wrapped: ActionsObject = {};

  for (const [actionName, actionFn] of Object.entries(actions)) {
    if (typeof actionFn !== "function") {
      wrapped[actionName] = actionFn;
      continue;
    }

    const stores = storeMapping[actionName];

    if (stores && stores.length > 0) {
      // Wrap action in a transaction
      wrapped[actionName] = async function (
        ...args: unknown[]
      ): Promise<unknown> {
        return withTransaction(stores, async () => {
          return (actionFn as (...args: unknown[]) => unknown).apply(
            actions,
            args,
          );
        });
      };
    } else {
      // No stores specified, keep action as-is
      wrapped[actionName] = (actionFn as (...args: unknown[]) => unknown).bind(
        actions,
      );
    }
  }

  return wrapped as T;
}

/**
 * Batch multiple store updates into a single synchronous operation
 * Useful for updates that must be atomic but without rollback
 *
 * @param fn - Function containing the updates
 *
 * @example
 * batchUpdates(() => {
 *   store1.set(value1);
 *   store2.set(value2);
 *   store3.update(v => ({ ...v, field: newValue }));
 * });
 */
export function batchUpdates(fn: () => void): void {
  // In Svelte, updates are already batched in the same tick
  // This function serves primarily as documentation of intent
  // and could be extended to support future optimizations
  fn();
}

/**
 * Creates a "transactional" store that supports savepoints
 * Allows partial rollbacks within the same transaction
 *
 * @param store - Store to wrap
 * @returns Store with savepoint methods
 */
export function createSavepointStore<T>(store: Writable<T>): SavepointStore<T> {
  const savepoints: Savepoint<T>[] = [];

  return {
    // Original store methods
    subscribe: store.subscribe,
    set: store.set,
    update: store.update,

    savepoint(name: string): void {
      savepoints.push({
        name,
        value: get(store),
        timestamp: Date.now(),
      });
    },

    rollbackTo(name: string): boolean {
      const index = savepoints.findIndex((sp) => sp.name === name);
      if (index === -1) {
        console.warn(`Savepoint "${name}" not found`);
        return false;
      }

      // Restore state
      store.set(savepoints[index].value);

      // Remove this savepoint and all following ones
      savepoints.splice(index);

      return true;
    },

    releaseSavepoint(name: string): void {
      const index = savepoints.findIndex((sp) => sp.name === name);
      if (index !== -1) {
        savepoints.splice(index, 1);
      }
    },

    getSavepoints(): SavepointInfo[] {
      return savepoints.map((sp) => ({
        name: sp.name,
        timestamp: sp.timestamp,
      }));
    },

    clearSavepoints(): void {
      savepoints.length = 0;
    },
  };
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  createTransaction,
  withTransaction,
  createTransactionalActions,
  batchUpdates,
  createSavepointStore,
};
